import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, X, LayoutGrid, Ban } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getEventoVisibleByIdParaSocio,
  hidratarEventosPublicos,
} from "@/data/adminEventosStore";
import { obtenerCompraContexto } from "@/data/comprasStore";
import EmptyState from "@/components/common/EmptyState";
import { SELECCION_MESA } from "@/constants/testIds";

const getEstado = (m) => {
  if (m.disponibles === 0) return "completa";
  if (m.disponibles <= 3) return "pocos";
  return "disponible";
};

const estiloMesa = {
  disponible:
    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/50 hover:bg-emerald-500/25",
  pocos:
    "bg-amber-400/20 text-amber-800 dark:text-amber-200 border-amber-500/60 hover:bg-amber-400/30",
  completa:
    "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-70",
};

const etiquetaEstado = {
  disponible: (m) => `${m.disponibles} disponibles`,
  pocos: (m) => `${m.disponibles} disponibles`,
  completa: () => "Completa",
};

export default function SeleccionMesa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hidratado, setHidratado] = useState(false);
  const [contexto, setContexto] = useState(null);
  const [mesaSel, setMesaSel] = useState(null);
  const [cantidad, setCantidad] = useState(1);

  useEffect(() => {
    hidratarEventosPublicos().finally(() => setHidratado(true));
  }, []);

  // Contexto autoritativo del backend: disponibilidad real por mesa + cupo
  // personal restante. Se refresca al entrar, al abrir/cerrar una mesa y al
  // volver a foco (por si otro socio compró mientras tanto).
  const refrescarContexto = useCallback(async () => {
    if (!id) return null;
    const ctx = await obtenerCompraContexto(id);
    if (ctx && !ctx.error) setContexto(ctx);
    return ctx;
  }, [id]);

  useEffect(() => {
    refrescarContexto();
    const onFocus = () => refrescarContexto();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refrescarContexto]);

  const evento = getEventoVisibleByIdParaSocio(id);

  if (!evento) {
    if (!hidratado) return null;
    return <Navigate to="/app" replace />;
  }

  const volverAlEvento = () => navigate(`/app/eventos/${evento.id}`);

  // Disponibilidad AUTORITATIVA: se toma del backend por número de mesa.
  // El plano local sólo aporta numero/capacidad; jamás usamos la capacidad
  // como disponibilidad.
  const dispBackend = new Map(
    (contexto?.mesas || []).map((m) => [Number(m.numero), m]),
  );
  const baseMesas = Array.isArray(evento.mesas) ? evento.mesas : [];
  const mesas = baseMesas
    .map((m) => {
      const b = dispBackend.get(Number(m.numero));
      return {
        numero: Number(m.numero),
        capacidad: b ? b.capacidad : Number(m.capacidad) || 0,
        disponibles: b ? b.disponibles : 0,
        vendible: b ? b.vendible : false,
        x: Number(m.x) || 0,
        y: Number(m.y) || 0,
      };
    })
    // Normalización visual determinista: orden estable por (y, x, número)
    // para que la retícula conserve la distribución del plano sin que las
    // mesas se empalmen jamás (el layout en grid garantiza separación).
    .sort((a, b) => a.y - b.y || a.x - b.x || a.numero - b.numero);

  const sinMesas = mesas.length === 0;
  const cupoRestante = contexto?.cupoRestante; // null = sin límite
  const limite = contexto?.limiteBoletosPorUsuario || 0;
  const confirmados = contexto?.confirmados || 0;
  const bloqueadoPorLimite =
    contexto && cupoRestante !== null && cupoRestante <= 0;

  // Máximo seleccionable = min(disponibilidad real, cupo personal restante).
  const maxSeleccionable = (m) => {
    const disp = m?.disponibles ?? 0;
    if (cupoRestante === null || cupoRestante === undefined) return disp;
    return Math.max(0, Math.min(disp, cupoRestante));
  };

  const abrirMesa = async (m) => {
    if (getEstado(m) === "completa") return;
    // Refresca antes de abrir para reflejar compras recientes.
    await refrescarContexto();
    setMesaSel(m);
    setCantidad(1);
  };
  const cerrar = () => {
    setMesaSel(null);
    refrescarContexto();
  };

  const topeActual = mesaSel ? maxSeleccionable(mesaSel) : 1;
  const dec = () => setCantidad((c) => Math.max(1, c - 1));
  const inc = () => setCantidad((c) => Math.min(Math.max(1, topeActual), c + 1));

  const precioNum = Number(String(evento.precio || "").replace(/[^\d]/g, "")) || 0;
  const total = precioNum * cantidad;
  const fmt = (n) => `$${n.toLocaleString("es-MX")} MXN`;

  const continuar = () => {
    navigate(`/app/eventos/${evento.id}/resumen`, {
      state: {
        mesa: mesaSel.numero,
        cantidad,
        precio: evento.precio,
        precioNum,
        total,
      },
    });
  };

  // Bloqueo por límite acumulado: no se permite selección de mesas.
  if (bloqueadoPorLimite) {
    return (
      <div
        data-testid={SELECCION_MESA.screen}
        className="flex flex-col gap-6 -mt-2"
      >
        <button
          type="button"
          onClick={volverAlEvento}
          aria-label="Regresar"
          className="self-start inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
        >
          <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          </span>
          Regresar
        </button>

        <div
          data-testid={SELECCION_MESA.limiteBloqueado}
          role="alert"
          className="rounded-2xl border hairline bg-destructive/5 dark:bg-destructive/10 p-6 flex items-start gap-4"
        >
          <span className="w-10 h-10 grid place-items-center rounded-xl bg-destructive/10 text-destructive shrink-0">
            <Ban className="w-5 h-5" strokeWidth={1.7} />
          </span>
          <div>
            <h1 className="font-heading text-xl text-foreground">
              Alcanzaste el límite de boletos
            </h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Ya tienes <strong>{confirmados}</strong> boleto(s) confirmado(s)
              para este evento y el límite es de <strong>{limite}</strong> por
              socio. No puedes adquirir más boletos.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            data-testid={SELECCION_MESA.btnVolverEvento}
            onClick={volverAlEvento}
            className="flex-1 rounded-full px-5 py-3 text-sm font-semibold border hairline bg-card text-foreground hover:bg-muted transition-colors"
          >
            Volver al evento
          </button>
          <button
            type="button"
            data-testid={SELECCION_MESA.btnMisBoletos}
            onClick={() => navigate("/app/mis-boletos")}
            className="flex-1 rounded-full px-5 py-3 text-sm font-semibold bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-90 transition-opacity"
          >
            Ir a Mis Boletos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={SELECCION_MESA.screen}
      className="flex flex-col gap-6 -mt-2"
    >
      <button
        type="button"
        onClick={volverAlEvento}
        aria-label="Regresar"
        className="self-start inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
      >
        <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
        </span>
        Regresar
      </button>

      <header className="animate-fade-up">
        <span className="overline text-accent">{evento.salon}</span>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">
          Selecciona una mesa
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-lg leading-relaxed">
          Selecciona la mesa donde deseas adquirir tus boletos. No
          seleccionarás lugares individuales.
        </p>
        {cupoRestante !== null && cupoRestante !== undefined && (
          <p
            data-testid={SELECCION_MESA.cupoRestante}
            className="mt-2 text-xs text-muted-foreground"
          >
            Puedes adquirir hasta <strong>{cupoRestante}</strong> boleto(s) más
            en este evento.
          </p>
        )}
      </header>

      {/* Leyenda */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-emerald-500/70 ring-1 ring-emerald-500/70" />
          Disponible
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-amber-400/80 ring-1 ring-amber-500/70" />
          Pocos lugares
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-muted ring-1 ring-border" />
          Completa
        </span>
      </div>

      {sinMesas ? (
        <EmptyState
          icon={LayoutGrid}
          eyebrow="Sin mesas"
          title="No hay mesas disponibles para este evento."
          description="Cuando el montaje del salón esté configurado, podrás elegir tu mesa aquí."
          testId={SELECCION_MESA.emptyMesas}
        />
      ) : (
        <div className="rounded-2xl border hairline bg-gradient-to-b from-muted/40 to-card overflow-hidden shadow-soft">
          {/* Escenario: elemento fijo, siempre en la parte superior. */}
          <div className="flex justify-center pt-5">
            <div className="px-6 py-1.5 rounded-full bg-primary/10 dark:bg-accent/15 text-[10px] tracking-widest uppercase text-primary dark:text-accent font-medium">
              Escenario
            </div>
          </div>

          {/* Retícula responsiva · separación garantizada · cero empalmes.
              El grid nativo distribuye las mesas sin superposición en móvil
              y escritorio, respetando el orden determinista por (y, x). */}
          <div className="p-5 sm:p-6 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 sm:gap-5 place-items-center">
            {mesas.map((m) => {
              const st = getEstado(m);
              return (
                <button
                  key={m.numero}
                  type="button"
                  data-testid={SELECCION_MESA.mesa(m.numero)}
                  disabled={st === "completa"}
                  onClick={() => abrirMesa(m)}
                  className={cn(
                    "w-full max-w-[6rem] aspect-square rounded-full border-2",
                    "flex flex-col items-center justify-center text-center",
                    "transition-all duration-200 active:scale-95",
                    estiloMesa[st],
                  )}
                >
                  <span className="font-heading text-sm sm:text-base leading-none">
                    Mesa {m.numero}
                  </span>
                  <span className="mt-1 text-[10px] sm:text-[11px] leading-none px-1">
                    {etiquetaEstado[st](m)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal */}
      {mesaSel && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={cerrar}
          />
          <div
            data-testid={SELECCION_MESA.modal}
            className="relative w-full max-w-md rounded-2xl border hairline bg-card p-6 md:p-8 shadow-elevated animate-fade-up"
          >
            <button
              type="button"
              onClick={cerrar}
              aria-label="Cerrar"
              className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full hover:bg-muted transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <h2
              data-testid={SELECCION_MESA.modalMesa}
              className="font-heading text-2xl text-foreground"
            >
              Mesa {mesaSel.numero}
            </h2>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-xl border hairline p-3">
                <span className="overline text-muted-foreground">Capacidad</span>
                <p
                  data-testid={SELECCION_MESA.modalCapacidad}
                  className="mt-1 text-foreground text-sm"
                >
                  {mesaSel.capacidad} personas
                </p>
              </div>
              <div className="rounded-xl border hairline p-3">
                <span className="overline text-muted-foreground">Disponibles</span>
                <p
                  data-testid={SELECCION_MESA.modalDisponibles}
                  className="mt-1 text-foreground text-sm"
                >
                  {mesaSel.disponibles}
                </p>
              </div>
            </div>

            <div className="mt-6">
              <span className="overline text-muted-foreground">
                Cantidad de boletos
              </span>
              <div className="mt-2 flex items-center justify-between rounded-full border hairline p-1.5 bg-background">
                <button
                  type="button"
                  data-testid={SELECCION_MESA.btnMenos}
                  onClick={dec}
                  disabled={cantidad <= 1}
                  className="w-10 h-10 grid place-items-center rounded-full bg-muted hover:bg-muted/70 disabled:opacity-40 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span
                  data-testid={SELECCION_MESA.cantidad}
                  className="font-heading text-2xl text-foreground"
                >
                  {cantidad}
                </span>
                <button
                  type="button"
                  data-testid={SELECCION_MESA.btnMas}
                  onClick={inc}
                  disabled={cantidad >= topeActual}
                  className="w-10 h-10 grid place-items-center rounded-full bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground disabled:opacity-40 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {cupoRestante !== null &&
                cupoRestante !== undefined &&
                topeActual < mesaSel.disponibles && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Límite por socio: puedes llevar hasta {topeActual} en esta
                    compra.
                  </p>
                )}
            </div>

            <div className="mt-6 border-t hairline pt-4 flex flex-col gap-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Precio por boleto</span>
                <span
                  data-testid={SELECCION_MESA.modalPrecio}
                  className="text-foreground"
                >
                  {evento.precio}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total</span>
                <span
                  data-testid={SELECCION_MESA.modalTotal}
                  className="font-heading text-lg text-foreground"
                >
                  {fmt(total)}
                </span>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid={SELECCION_MESA.btnCancelar}
                onClick={cerrar}
                className="flex-1 rounded-full px-5 py-3 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                data-testid={SELECCION_MESA.btnContinuar}
                onClick={continuar}
                disabled={topeActual < 1}
                className="flex-1 rounded-full px-5 py-3 text-sm font-semibold bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-90 disabled:opacity-40 transition-opacity"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
